import { useId, useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Container, Card, Form, Button, Alert, Row, Col, Table, Spinner } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { Helmet } from 'react-helmet-async';
import api from '../services/api';
import CollapsibleHelpSection from '../components/common/CollapsibleHelpSection';
import { getAxiosOrErrorMessage } from '../utils/error.utils';
import { DEFAULT_USAGE_RECORD_QUANTITY } from '../constants/sample.constants';
import { canSampleAcceptUsageRecord } from '../utils/sampleUsageRecords';
import { softBreakIdentifier } from '../utils/softBreakIdentifier';
import { formatSampleQuantityWithUnit } from '../utils/sampleUnitLabels';
import BaseDateTimeField from '../components/samples/BaseDateTimeField';

const usageRecordSchema = (t: (key: string, options?: any) => string) => z.object({
  usageDate: z.string().min(1, t('usage_record.usage_date_required', { defaultValue: '使用日は必須です' })),
  userName: z.string().min(1, t('usage_record.user_name_required', { defaultValue: '使用者は必須です' })).max(255, t('usage_record.user_name_max_length', { defaultValue: '使用者名は255文字以内で入力してください' })),
  quantity: z.number().int().min(0, t('usage_record.quantity_min_error', { defaultValue: '使用数量は0以上である必要があります' })),
  notes: z.string().max(1000).optional(),
});

type UsageRecordFormData = z.infer<ReturnType<typeof usageRecordSchema>>;

export default function UsageRecord() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const fieldId = useId();
  const [searchParams] = useSearchParams();
  // @ts-ignore - 将来の拡張用に保持
  const navigate = useNavigate();
  const token = searchParams.get('token');
  const [success, setSuccess] = useState<string>('');
  const [formError, setFormError] = useState<string>('');

  // トークンからサンプル情報を取得
  const { data: sample, isLoading: isLoadingSample, isError: isSampleError } = useQuery({
    queryKey: ['sample-by-token', token],
    queryFn: async () => {
      if (!token) {
        throw new Error(t('usage_record.token_required', { defaultValue: 'トークンが必要です' }));
      }
      const response = await api.get(`/samples/usage-record/token?token=${encodeURIComponent(token)}`);
      return response.data.data;
    },
    enabled: !!token,
    retry: false,
  });

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<UsageRecordFormData>({
    resolver: zodResolver(usageRecordSchema(t)),
    defaultValues: {
      usageDate: format(new Date(), "yyyy-MM-dd'T'HH:mm"),
      userName: '',
      quantity: DEFAULT_USAGE_RECORD_QUANTITY,
      notes: '',
    },
  });
  const usageDate = watch('usageDate');

  const addUsageRecordMutation = useMutation({
    mutationFn: async (data: UsageRecordFormData) => {
      const response = await api.post(`/samples/usage-record/token?token=${encodeURIComponent(token!)}`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sample-by-token', token] });
      setFormError('');
      setSuccess(t('usage_record.success', { defaultValue: '使用記録を登録しました' }));
      reset({
        usageDate: format(new Date(), "yyyy-MM-dd'T'HH:mm"),
        userName: '',
        quantity: DEFAULT_USAGE_RECORD_QUANTITY,
        notes: '',
      });
    },
  });

  const onSubmit = async (data: UsageRecordFormData) => {
    setFormError('');
    if (
      sample &&
      sample.remainingQuantity != null &&
      data.quantity > sample.remainingQuantity
    ) {
      setFormError(
        t('usage_record.quantity_exceeds_remaining', { remaining: sample.remainingQuantity })
      );
      return;
    }
    await addUsageRecordMutation.mutateAsync(data);
  };

  if (!token) {
    return (
      <>
        <Helmet>
          <meta name="robots" content="noindex,nofollow" />
          <title>{t('usage_record.title', { defaultValue: '使用記録' })}</title>
        </Helmet>
        <Container className="mt-4">
          <Alert variant="danger">
            <Alert.Heading>{t('usage_record.token_required', { defaultValue: 'トークンが必要です' })}</Alert.Heading>
            <p>{t('usage_record.token_required_desc', { defaultValue: 'このページにアクセスするには有効なトークンが必要です。' })}</p>
          </Alert>
        </Container>
      </>
    );
  }

  if (isLoadingSample) {
    return (
      <>
        <Helmet>
          <meta name="robots" content="noindex,nofollow" />
        </Helmet>
        <Container className="mt-4">
          <div className="text-center mt-5">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">{t('common.loading')}</span>
            </Spinner>
          </div>
        </Container>
      </>
    );
  }

  if (isSampleError || !sample) {
    return (
      <>
        <Helmet>
          <meta name="robots" content="noindex,nofollow" />
        </Helmet>
        <Container className="mt-4">
          <Alert variant="danger">
            <Alert.Heading>{t('usage_record.invalid_token', { defaultValue: '無効なトークンです' })}</Alert.Heading>
            <p>{t('usage_record.invalid_token_desc', { defaultValue: 'トークンが無効または期限切れです。' })}</p>
          </Alert>
        </Container>
      </>
    );
  }

  const canAddUsage = canSampleAcceptUsageRecord(sample);

  return (
    <>
      <Helmet>
        <meta name="robots" content="noindex,nofollow" />
        <title>{t('usage_record.title', { defaultValue: '使用記録' })}</title>
      </Helmet>
      <Container className="mt-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1>
          <i className="fas fa-clipboard-list text-primary me-2"></i>
          {t('usage_record.title', { defaultValue: '使用記録' })}
        </h1>
      </div>

      <CollapsibleHelpSection
        titleKey="usage_record.how_to_use_title"
        descriptionKey="usage_record.how_to_use_desc"
        stepsKey={[
          'usage_record.how_to_use_step1',
          'usage_record.how_to_use_step2',
          'usage_record.how_to_use_step3',
          'usage_record.how_to_use_step4',
        ]}
        noteKey="usage_record.note"
        storageKey="usage_record_page"
        defaultExpanded={false}
      />

      {success && (
        <Alert variant="success" dismissible onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {formError && (
        <Alert variant="danger" dismissible onClose={() => setFormError('')}>
          {formError}
        </Alert>
      )}

      {addUsageRecordMutation.isError && (
        <Alert variant="danger" dismissible onClose={() => addUsageRecordMutation.reset()}>
          {(() => {
            const raw =
              getAxiosOrErrorMessage(addUsageRecordMutation.error) ||
              t('usage_record.submit_failed', { defaultValue: '使用記録の登録に失敗しました' });
            const isKey =
              typeof raw === 'string' &&
              (raw.startsWith('sample.') ||
                raw.startsWith('usage_record.') ||
                raw.startsWith('common.') ||
                raw.startsWith('api.error.'));
            return isKey ? t(raw, { defaultValue: raw }) : raw;
          })()}
        </Alert>
      )}

      {!canAddUsage && (
        <Alert variant="warning">
          {sample.disposalDate ? t('sample.already_disposed') : t('sample.cannot_add_usage')}
        </Alert>
      )}

      <Row>
        <Col md={6}>
          <Card>
            <Card.Header>
              <Card.Title className="mb-0">{t('usage_record.form_title', { defaultValue: '使用記録フォーム' })}</Card.Title>
            </Card.Header>
            <Card.Body>
              <fieldset disabled={!canAddUsage || addUsageRecordMutation.isPending}>
              <Form onSubmit={handleSubmit(onSubmit)}>
                <Form.Group className="mb-3">
                  <Form.Label htmlFor={`${fieldId}-current-quantity`}>
                    {t('usage_record.current_quantity', { defaultValue: '現在の数量' })}
                  </Form.Label>
                  <Form.Control
                    id={`${fieldId}-current-quantity`}
                    name="usageRecordCurrentQuantityDisplay"
                    type="text"
                    value={formatSampleQuantityWithUnit(
                      sample.remainingQuantity ?? sample.initialQuantity ?? 0,
                      sample.unit,
                      t
                    )}
                    disabled
                    readOnly
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <BaseDateTimeField
                    id={`${fieldId}-usage-date`}
                    label={t('usage_record.usage_date', { defaultValue: '使用日' })}
                    required
                    value={usageDate}
                    onChange={(v) =>
                      setValue('usageDate', v, { shouldValidate: true, shouldDirty: true })
                    }
                    isInvalid={!!errors.usageDate}
                  />
                  {errors.usageDate ? (
                    <div className="invalid-feedback d-block">{errors.usageDate.message}</div>
                  ) : null}
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label htmlFor={`${fieldId}-user-name`}>
                    {t('usage_record.user_name', { defaultValue: '使用者' })} <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    id={`${fieldId}-user-name`}
                    type="text"
                    {...register('userName')}
                    isInvalid={!!errors.userName}
                    placeholder={t('usage_record.user_name_placeholder', { defaultValue: '使用者名を入力してください' })}
                  />
                  {errors.userName && (
                    <Form.Control.Feedback type="invalid">
                      {errors.userName.message}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label htmlFor={`${fieldId}-quantity`}>
                    {t('usage_record.quantity_used', { defaultValue: '使用数量' })} <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    id={`${fieldId}-quantity`}
                    type="number"
                    {...register('quantity', { valueAsNumber: true })}
                    isInvalid={!!errors.quantity}
                    min={0}
                    max={
                      sample.remainingQuantity != null ? sample.remainingQuantity : undefined
                    }
                  />
                  {errors.quantity && (
                    <Form.Control.Feedback type="invalid">
                      {errors.quantity.message}
                    </Form.Control.Feedback>
                  )}
                  <Form.Text className="text-muted">
                    {t('usage_record.quantity_note', {
                      defaultValue: '0数量の使用記録も許可されます（使用確認のみの場合）',
                    })}
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label htmlFor={`${fieldId}-notes`}>{t('usage_record.notes', { defaultValue: '備考' })}</Form.Label>
                  <Form.Control
                    id={`${fieldId}-notes`}
                    as="textarea"
                    rows={3}
                    {...register('notes')}
                    isInvalid={!!errors.notes}
                    placeholder={t('usage_record.notes_placeholder', { defaultValue: '備考を入力してください（任意）' })}
                  />
                  {errors.notes && (
                    <Form.Control.Feedback type="invalid">
                      {errors.notes.message}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>

                <Button
                  variant="primary"
                  type="submit"
                  disabled={!canAddUsage || addUsageRecordMutation.isPending}
                >
                  {addUsageRecordMutation.isPending
                    ? t('common.processing', { defaultValue: '処理中...' })
                    : t('usage_record.submit', { defaultValue: '記録' })}
                </Button>
              </Form>
              </fieldset>
            </Card.Body>
          </Card>
        </Col>

        <Col md={6}>
          <Card>
            <Card.Header>
              <Card.Title className="mb-0">{t('usage_record.sample_details', { defaultValue: 'サンプル詳細' })}</Card.Title>
            </Card.Header>
            <Card.Body>
              <Table bordered size="sm">
                <tbody>
                  <tr>
                    <th style={{ width: '40%' }}>{t('sample.id', { defaultValue: 'サンプルID' })}</th>
                    <td>{sample.id}</td>
                  </tr>
                  <tr>
                    <th>{t('sample.name', { defaultValue: 'サンプル名' })}</th>
                    <td>{softBreakIdentifier(sample.name, t)}</td>
                  </tr>
                  {sample.sampleCode && (
                    <tr>
                      <th>{t('sample.sample_code', { defaultValue: 'サンプルコード' })}</th>
                      <td>{sample.sampleCode}</td>
                    </tr>
                  )}
                  {sample.sampleType && (
                    <tr>
                      <th>{t('sample.sample_type', { defaultValue: 'タイプ' })}</th>
                      <td>{sample.sampleType}</td>
                    </tr>
                  )}
                  {sample.bslLevel && (
                    <tr>
                      <th>{t('sample.bsl_level', { defaultValue: 'BSLレベル' })}</th>
                      <td>{sample.bslLevel}</td>
                    </tr>
                  )}
                  {sample.facility && (
                    <tr>
                      <th>{t('sample.facility', { defaultValue: '施設名' })}</th>
                      <td>{sample.facility.name}</td>
                    </tr>
                  )}
                  {sample.storageRoom && (
                    <tr>
                      <th>{t('sample.storage_room', { defaultValue: '保管庫名' })}</th>
                      <td>{sample.storageRoom.name}</td>
                    </tr>
                  )}
                  <tr>
                    <th>{t('sample.remaining_quantity', { defaultValue: '残数量' })}</th>
                    <td>
                      {formatSampleQuantityWithUnit(
                        sample.remainingQuantity ?? sample.initialQuantity ?? 0,
                        sample.unit,
                        t
                      )}
                    </td>
                  </tr>
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
    </>
  );
}

